// Which open dialog owns the keyboard.
//
// Run: deno test --allow-read src/lib/dialogStack.test.ts
// Run: npm run verify:dialog-stack
//
// `Modal` (src/components/ui/ToastModal.tsx) listens for Escape and Tab on
// `document`, because a dialog must catch those keys wherever focus happens
// to be. With two dialogs open at once every listener fired, so Escape on a
// confirm dialog also closed the editor underneath it and the outer focus
// trap fought the inner one. This module answers the one question that
// fixes it: of the dialogs currently open, is this one on top?
//
// PUSHING AN ID THAT IS ALREADY PRESENT DOES NOTHING, AND THAT IS THE WHOLE
// POINT. A React effect can re-run for reasons that have nothing to do with
// a dialog opening or closing, and a naive re-push would move the dialog to
// the end of the stack. For an OUTER dialog that means it becomes "topmost"
// while an inner confirm is still on screen, so Escape closes the editor
// underneath and discards unsaved edits, which is exactly the bug the stack
// exists to prevent, reintroduced through effect churn rather than through
// unconditional listeners. `Modal` now also runs its effect only on a real
// open or close, so this should not arise; keeping the invariant here as
// well means a future caller cannot reintroduce it from the outside.
//
// Plain arrays, mutated in place, because the consumer is a module-level
// singleton shared by every `Modal` on the page rather than React state.

/** Ordered oldest first, so the last entry is the dialog on top. */
export type DialogStack = string[];

export function createDialogStack(): DialogStack {
  return [];
}

/**
 * Records `id` as open. A no-op when `id` is already in the stack, which
 * preserves the order dialogs were genuinely opened in.
 */
export function pushDialog(stack: DialogStack, id: string): void {
  if (stack.includes(id)) return;
  stack.push(id);
}

/** Records `id` as closed. A no-op when `id` is not in the stack. */
export function removeDialog(stack: DialogStack, id: string): void {
  const index = stack.lastIndexOf(id);
  if (index !== -1) stack.splice(index, 1);
}

/**
 * Whether `id` is the dialog on top, and so the one that should act on a key
 * press. False for an empty stack, so a stale listener that outlives its own
 * removal goes quiet rather than claiming ownership.
 */
export function isTopmostDialog(stack: readonly string[], id: string): boolean {
  return stack.length > 0 && stack[stack.length - 1] === id;
}

/**
 * The one stack the whole page shares.
 *
 * "Am I on top?" is a question about the page, not about a React subtree, so
 * there can only be one answer and therefore only one stack. This used to be
 * a module-level constant inside ToastModal.tsx, which was fine while `Modal`
 * was the only dialog listening on `document`. It is not the only one: the
 * P&L widget editor drawer and the drill-down panel are hand-rolled dialogs
 * with their own `document` listeners, and a second stack would make each set
 * of dialogs believe it was topmost while the other was on screen, so one
 * Escape would close both. The singleton lives here instead, next to the
 * rules that operate on it.
 *
 * Exported as a plain array rather than behind a getter because the four
 * functions above are the entire supported API and they take the stack as an
 * argument; a caller reaching past them is a caller to fix, not a case to
 * defend against with an accessor.
 */
export const openDialogStack: DialogStack = createDialogStack();
